import { useTranslate } from 'react-admin';
import { Alert, Box, Stack, Typography } from '@mui/material';

import { formatInstant, formatUsdcAmount } from '../vaults/helpers';
import { OFFICIAL_VAULTS_NS as NS } from './constants';
import { useFaucetTransfers } from './useFaucetTransfers';

/**
 * XBIT-4348: the vault's recent faucet requests, newest first. The send reply is always DRAFT and a consumer decides
 * the rest, so this is where an operator sees that a transfer FAILED and why (the engine's curated reason, shown as
 * sent, in red). A read that fails says so in one line and nothing else on the drawer changes.
 */
export const FaucetTransfers = ({ vaultId }: { vaultId: string }) => {
  const translate = useTranslate();
  const { rows, loading, error } = useFaucetTransfers(vaultId, true);

  return (
    <Stack spacing={1.5} data-testid="faucet-transfers">
      <Typography variant="subtitle2">{translate(`${NS}.faucet.transfers.title`)}</Typography>
      {loading && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {translate(`${NS}.faucet.transfers.loading`)}
        </Typography>
      )}
      {error && <Alert severity="warning">{translate(`${NS}.faucet.transfers.error`)}</Alert>}
      {!loading && !error && rows.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {translate(`${NS}.faucet.transfers.empty`)}
        </Typography>
      )}
      {rows.map((row) => (
        <Box key={row.key} component="div" sx={{ display: 'grid', gap: 0.25 }}>
          <Typography variant="body2">
            {row.createdAt ? formatInstant(row.createdAt) : '—'} · {formatUsdcAmount(row.amount)}{' '}
            USDC · {translate(`${NS}.faucet.transfers.state.${row.state}`, { _: row.state })}
          </Typography>
          {row.failed && (
            <Typography variant="body2" color="error" role="alert">
              {row.failureReason ?? translate(`${NS}.faucet.transfers.noReason`)}
              {row.failureCode ? ` (${row.failureCode})` : ''}
            </Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
};
